import React, { useContext, useEffect, useState } from "react";
import { Filters } from "react-table";
import { AuthContext } from "../Auth/AuthProvider";
import { ProfileContext } from "../Auth/ProfileProvider";
import Resident from "../Residents/Models/Resident";
import { abpnClient } from "../Services/AbpnClient";
import { deepCopy } from "../Shared/Utility/DeepCopy";

interface IActiveRosterProviderValue {
    roster: Array<Resident>,
    filteredRoster: Array<Resident>,
    filters: Filters<object>,
    isRosterLoading: boolean,
    updateFilteredRoster: (filteredResidents: Array<Resident>) => void,
    updateFilters: (filters: Filters<object>) => void,
    updateResident: (resident: Resident) => void,
    reloadRoster: () => void
}

export const ActiveRosterContext = React.createContext<IActiveRosterProviderValue>(null);

export const ActiveRosterProvider = ({ children }: { children: JSX.Element }) => {
    const [roster, setRoster] = useState<Array<Resident>>(null);
    const [filteredRoster, setFilteredRoster] = useState<Array<Resident>>(null);
    const [filters, setFilters] = useState<Filters<object>>([]);
    const [isRosterLoading, setIsRosterLoading] = useState<boolean>(false);
    const [reload, setReload] = useState<boolean>(false);
    const { user } = useContext(ProfileContext);
    const authContext = useContext(AuthContext);

    const updateFilters = (filters: Filters<object>) => {
        setFilters(filters);
    }

    const updateFilteredRoster = (filteredResidents: Array<Resident>) => {
        setFilteredRoster(filteredResidents);
    }

    const updateResident = (resident: Resident) => {
        let updatedRoster = deepCopy(roster);
        let index = roster?.findIndex(
            (_) => _.physicianId === resident.physicianId
        );
        if (index !== -1) {
            updatedRoster[index] = resident;
            setRoster(updatedRoster);
        }

        let updatedFilteredRoster = deepCopy(filteredRoster);
        let filteredIndex = filteredRoster?.findIndex(
            (_) => _.physicianId === resident.physicianId
        );
        if (filteredIndex !== -1) {
            updatedFilteredRoster[filteredIndex] = resident;
            setFilteredRoster(updatedFilteredRoster);
        }
    }

    const sortPgyDesc = (a: Resident, b: Resident) => {
        var aYear = a.currentPostGraduateYear ?? 0;
        var bYear = b.currentPostGraduateYear ?? 0;

        return aYear > bYear ? -1 : 1;
    };

    const sortLastName = (a: Resident, b: Resident) => {
        return a.name?.lastName > b.name?.lastName ? 1 : -1;
    };

    const reloadRoster = () => {
        setReload(true);
    }

    useEffect(() => {
        const loadRoster = async () => {
            setIsRosterLoading(true);
            let userProgramTypeId = user.trainingPrograms.find(
                (_) => _.trainingProgramId === user.selectedTrainingProgramId
              ).trainingProgramTypeId;
            let url = `/api/dashboard/GetCurrentResidents?programTypeId=${userProgramTypeId}`;
            let rosterResult = await abpnClient.get<Array<Resident>>(url, authContext);
            let sortedResult = rosterResult.sort(sortLastName).sort(sortPgyDesc);
            setRoster(sortedResult);
            setFilters([]);
            setFilteredRoster(sortedResult);
            setIsRosterLoading(false);
            setReload(false)
        };

        loadRoster();
    }, [authContext, user.selectedTrainingProgramId, reload])

    return <ActiveRosterContext.Provider value={{ roster, filteredRoster, filters, isRosterLoading, updateFilteredRoster, updateFilters, updateResident, reloadRoster }}> {children}</ ActiveRosterContext.Provider>;
}